import { useCallback, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { camelCase, isUndefined, omit, isEqual } from "lodash";

import { updateFieldValue } from "../../reducers/form/fields";
import { getDisplayName } from "../Utils/getDisplayName";
import { useForm } from "../Form/withFormStore";

export default function inputConnect(WrappedComponent, options = {}) {
  const InputConnect = props => {
    const { fieldName, onUpdate } = props;
    const formData = useForm();
    const formKey = props.formKey || formData?.formKey;
    const passthroughProps = omit(props, "inputValues");

    const field = useSelector(({ forms }) => forms[formKey]?.fields[fieldName] || {});

    const inputValuesRef = useRef(props.inputValues);

    const processInputValues = useCallback(() => {
      const inputValues = {};

      Object.keys(options.fields || {}).forEach(key => {
        inputValues[key] = field[key];

        if (isUndefined(inputValues[key])) {
          const defaultKey = camelCase(`default_${key}`);

          inputValues[key] = props[defaultKey];
        }

        if (isUndefined(inputValues[key])) {
          inputValues[key] = options.fields[key];
        }
      });

      if (!isEqual(inputValuesRef.current, inputValues)) {
        inputValuesRef.current = inputValues;
      }

      return inputValuesRef.current;
    }, [field, props]);

    const inputValuesRefBeforeProcess = inputValuesRef.current;
    const inputValues = processInputValues();

    const dispatch = useDispatch();

    // This is used a value cache to avoid useless value updates. It allows us to use
    // lodash's `isEqual` function to make a deep comparison between existing `value`
    // and the current `value` that's updated in the `handleUpdate` callback.
    const previousValueRef = useRef();

    // Ensure that any external field value update is propagated to our previousValueRef
    // cache
    useEffect(() => {
      if (isEqual(previousValueRef.current, field)) return;

      previousValueRef.current = field;
    }, [field]);

    const handleUpdate = useCallback(
      (key, value) => {
        // Avoid multiple same value updates because of multiple callback calls during the
        // same update loop.
        if (isEqual(previousValueRef.current, value)) return;

        if (onUpdate) {
          onUpdate(formKey, key, value);
        } else {
          previousValueRef.current = value;
          dispatch(updateFieldValue(formKey, key, value));
        }
      },
      [dispatch, formKey, onUpdate]
    );

    const updateValue = useCallback(
      (value, targetFieldName = fieldName) => {
        // Pass a copy of the value to the action creator so we ensure that the
        // initial value can't get mutated
        handleUpdate(targetFieldName, { ...value });
      },
      [fieldName, handleUpdate]
    );

    useEffect(() => {
      if (!isEqual(inputValues, inputValuesRefBeforeProcess)) {
        updateValue(inputValues);
      }
    }, [updateValue, inputValues, inputValuesRefBeforeProcess]);

    return (
      <WrappedComponent
        {...passthroughProps}
        {...inputValues}
        fieldName={fieldName}
        updateValue={updateValue}
      />
    );
  };

  InputConnect.displayName = `InputConnect(${getDisplayName(WrappedComponent)})`;

  return InputConnect;
}
